import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';

import { AutoReplyChip } from './auto-reply-notice';
import { STATUS_BADGE_BG } from './message-status';

import { DateDisplay } from '@/components/common/date-display';
import { OwnerHeader } from '@/components/common/owner-header';
import { PersonChip } from '@/components/common/person-chip';
import { Text, useTheme } from '@/components/ui';
import type { InboxLabel, InboxMessage } from '@/services/data-service';
import { isUnfiledMessage, MESSAGE_STATUS_LABEL, type MessageStatus } from '@/utils/inbox-triage';
import { deliveryBannerFor } from '@/utils/message-delivery';

/**
 * Amber, deliberately NOT the theme's error red.
 *
 * The red in this UI already means "we don't know whose this is" (the triage queue).
 * "Nobody has acted on this" is a different problem, and painting both the same colour
 * would merge two queues that get worked by different people.
 */
const UNFILED_BADGE_BG = '#B26A00';

/**
 * The away-state colours live in `./message-status` now, beside the icons and wording, so
 * the rail row, the pane banner and this badge cannot drift apart. ARCHIVED keeps the grey
 * it had: it is the opposite of a to-do marker — it says this row needs nothing from
 * anyone — and the other two badges here shout for attention, so it has to read as settled
 * or the Archived view looks like yet another queue of work.
 */

/** Small coloured chip, the same shape everywhere a label is shown. */
export const LabelChip: React.FC<{ label: InboxLabel; compact?: boolean }> = ({ label, compact }) => (
	<View
		style={{
			paddingHorizontal: compact ? 6 : 8,
			paddingVertical: compact ? 1 : 3,
			borderRadius: 10,
			backgroundColor: `${label.color ?? '#607d8b'}22`,
		}}
	>
		<Text style={{ fontSize: compact ? 10 : 11, fontWeight: '700', color: label.color ?? '#607d8b' }}>
			{label.name}
		</Text>
	</View>
);

export interface MessageListProps {
	messages: InboxMessage[];
	selectedId?: string | null;
	onSelect: (message: InboxMessage) => void;
	emptyText: string;
	loading?: boolean;
	/**
	 * This list is INSIDE another scroller, so it must not be one itself.
	 *
	 * Standalone, the list owns the pane and `flex: 1` is what gives it height. Nested in a
	 * page that already scrolls, that same `flex: 1` stretches it to the full viewport and
	 * leaves a block of empty space between the last row and whatever follows — which is
	 * exactly what the Needs Review screen showed between its two sections.
	 */
	embedded?: boolean;
}

/**
 * The middle pane: one row per message, newest first.
 *
 * A row shows its LABELS rather than a status word — labels are the categorisation now,
 * and a message can wear several, which a single status field could never express.
 */
export const MessageList: React.FC<MessageListProps> = ({
	messages,
	selectedId,
	onSelect,
	emptyText,
	loading = false,
	embedded = false,
}) => {
	const theme = useTheme();
	const [hoveredId, setHoveredId] = useState<string | null>(null);

	if (messages.length === 0) {
		return (
			<View style={{ flex: embedded ? undefined : 1, alignItems: 'center', justifyContent: 'center', padding: 32 }}>
				<Ionicons name="mail-open-outline" size={40} color={theme.colors.outline} />
				<Text style={{ marginTop: 12, color: theme.colors.onSurfaceVariant, textAlign: 'center' }}>
					{loading ? 'Loading…' : emptyText}
				</Text>
			</View>
		);
	}

	// Embedded, the page's own ScrollView owns the axis — a plain View wraps to its content.
	const Container = embedded ? View : ScrollView;
	const containerProps = embedded
		? {}
		: { style: { flex: 1 }, contentContainerStyle: { flexGrow: 1 } };

	return (
		<Container {...containerProps} testID="inbox-message-list">
			{messages.map((message) => {
				const selected = selectedId === message.id;
				const unread = !message.readAt;
				// Same rule the ?unfiled=1 filter applies server-side, so the badge and the
				// list can never disagree about what counts as outstanding.
				const unfiled = isUnfiledMessage(message);
				/**
				 * Where the message lives, if it is anywhere but the inbox (migration 261).
				 *
				 * Replaces the `archivedAt` boolean: there are four states now and only one of
				 * them belongs on the working list, so a row in any view other than the inbox
				 * has to say WHICH away-state it is in.
				 */
				const away = (message.status ?? 'open') !== 'open'
					? (message.status as Exclude<MessageStatus, 'open'>)
					: null;
				const who = message.submitterName || message.fromName || message.fromAddress || 'Unknown sender';
				return (
					<Pressable
						key={message.id}
						onPress={() => onSelect(message)}
						onHoverIn={() => setHoveredId(message.id)}
						onHoverOut={() => setHoveredId((prev) => (prev === message.id ? null : prev))}
						testID={`inbox-message-${message.id}`}
						style={{
							flexDirection: 'row',
							alignItems: 'flex-start',
							paddingVertical: 10,
							paddingHorizontal: 14,
							borderBottomWidth: 1,
							borderBottomColor: theme.colors.outlineVariant ?? theme.colors.outline,
							borderLeftWidth: 3,
							borderLeftColor: selected ? theme.colors.primary : 'transparent',
							backgroundColor: selected
								? theme.colors.primaryContainer
								: hoveredId === message.id ? theme.colors.surfaceVariant : theme.colors.surface,
						}}
					>
						<Ionicons
							name={unread ? 'mail-unread-outline' : 'mail-open-outline'}
							size={18}
							color={unread ? theme.colors.primary : theme.colors.onSurfaceVariant}
							style={{ marginRight: 10, marginTop: 2 }}
						/>
						<View style={{ flex: 1, minWidth: 0 }}>
							<OwnerHeader
								testID={`inbox-message-${message.id}-owner`}
								associationName={message.associationName}
								managerId={message.managerId}
								managerName={message.managerName}
								managerColor={message.managerColor}
								trailing={(
									<>
										{/* Who has it. An assigned message is still unfiled until it
											produces something, so this and the badge can both show. */}
										<PersonChip
											testID={`inbox-message-${message.id}-assignee`}
											id={message.assignedTo}
											name={message.assignedToName}
											color={message.assignedToColor}
											fallback={message.assignedTo ? 'Assigned' : undefined}
										/>
										{/*
											An away-state wins over UNFILED. An archived message that produced
											nothing is still literally unfiled, but showing both would tell
											the reader to act on something somebody has explicitly put away —
											and in the Archived view every row would wear an amber pill.

											The testID stays `-archived` for every away-state rather than
											becoming per-state: it identifies the SLOT, and the existing
											badge-precedence tests assert on it.
										*/}
										{away ? (
											<View
												testID={`inbox-message-${message.id}-archived`}
												style={{
													flexShrink: 0,
													paddingHorizontal: 6,
													paddingVertical: 1,
													borderRadius: 4,
													backgroundColor: STATUS_BADGE_BG[away],
												}}
											>
												<Text style={{ fontSize: 10, fontWeight: '800', letterSpacing: 0.4, color: '#fff' }}>
													{MESSAGE_STATUS_LABEL[away].toUpperCase()}
												</Text>
											</View>
										) : null}
										{/* The inverse of the "· linked" marker below. A pill, not another
											muted caption: it is the one thing on the row that says DO
											SOMETHING and has to survive being surrounded by grey metadata. */}
										{unfiled && !away ? (
											<View
												testID={`inbox-message-${message.id}-unfiled`}
												style={{
													flexShrink: 0,
													paddingHorizontal: 6,
													paddingVertical: 1,
													borderRadius: 4,
													backgroundColor: UNFILED_BADGE_BG,
												}}
											>
												<Text style={{ fontSize: 10, fontWeight: '800', letterSpacing: 0.4, color: '#fff' }}>
													UNFILED
												</Text>
											</View>
										) : null}
										{/*
											#595's chip, for both: a rule that answered this by itself
											(shown in every view — an email went out that no PM wrote), or
											a composed letter of ours that was held or went to the test
											mailbox (#555). `deliveryBannerFor` picks by direction.
										*/}
										{(() => {
											const banner = deliveryBannerFor(message);
											return banner ? (
												<AutoReplyChip
													testID={banner.noun === 'Letter'
														? `inbox-message-${message.id}-delivery`
														: `inbox-message-${message.id}-auto-reply`}
													info={banner.attempts[0]}
													noun={banner.noun}
												/>
											) : null;
										})()}
									</>
								)}
							/>
							<View style={{ flexDirection: 'row', alignItems: 'center' }}>
								<Text
									numberOfLines={1}
									style={{ flex: 1, minWidth: 0, fontWeight: unread ? '800' : '600', color: theme.colors.onSurface }}
								>
									{who}
								</Text>
								{message.createdAt ? (
									<DateDisplay
										date={new Date(message.createdAt)}
										dateOnly
										variant="bodySmall"
										style={{ color: theme.colors.onSurfaceVariant, marginLeft: 8 }}
									/>
								) : null}
							</View>
							<Text numberOfLines={1} style={{ fontWeight: '600', color: theme.colors.onSurface, marginTop: 1 }}>
								{message.subject || '(no subject)'}
							</Text>
							<Text numberOfLines={1} style={{ color: theme.colors.onSurfaceVariant, marginTop: 1 }}>
								{message.propertyAddress || message.submitterEmail || message.bodyText || '—'}
							</Text>
							<View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
								{message.labels.map((label) => (
									<LabelChip key={label.id} label={label} compact />
								))}
								{message.accountNumber ? (
									<Text style={{ fontSize: 11, color: theme.colors.onSurfaceVariant, marginLeft: 2 }}>
										{message.accountNumber}
									</Text>
								) : null}
								{/* One link means it produced something; say so on the row so a
									 staffer doesn't open it to find out. */}
								{message.links.length > 0 ? (
									<Text style={{ fontSize: 11, color: theme.colors.primary, fontWeight: '600', marginLeft: 2 }}>
										· linked
									</Text>
								) : null}
							</View>
						</View>
					</Pressable>
				);
			})}
		</Container>
	);
};

export default MessageList;
